import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import loginImage from "../../../assests/auth pages.png";
import googleLogo from "../../../assests/google-logo.png";
import { validateEmail, validatePassword } from "../../common/Validation";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { LOGIN_URL } from "../../../config/config";
import {
  setEmail,
  setPassword,
  setEmailError,
  setPasswordError,
  setLoginError,
  setLoginSuccess,
  setTokens,
  setUser,
} from "../../../slices/authSlice";
import { useGoogleLogin } from '@react-oauth/google';
import { jwtDecode } from "jwt-decode";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { 
    email, 
    password, 
    emailError, 
    passwordError, 
    loginError, 
    loginSuccess,
    accessToken 
  } = useSelector((state) => state.auth);

  useEffect(() => {
    if (accessToken) {
      navigate("/login");
    }
  }, [accessToken, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const emailValidationError = validateEmail(email);
    const passwordValidationError = validatePassword(password);

    dispatch(setEmailError(emailValidationError));
    dispatch(setPasswordError(passwordValidationError));

    if (!emailValidationError && !passwordValidationError) {
      try {
        const response = await fetch(LOGIN_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, password }),
        });

        const responseText = await response.text();
        console.log("Backend response text:", responseText);

        let data;
        try {
          data = JSON.parse(responseText);
        } catch (error) {
          console.error("Error parsing JSON:", error);
          handleLoginError("Server response is not valid JSON. Please try again later.");
          return;
        }

        if (response.ok && data.status === 1) {
          handleLoginSuccess(data.data);
        } else {
          handleLoginError(data.message || "Login failed. Please try again.");
        }
      } catch (error) {
        console.error("Error during login:", error);
        handleLoginError("Something went wrong. Please try again.");
      }
    }
  };

  const login = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: async (codeResponse) => {
      console.log("Google login successful. Code Response:", codeResponse);
      try {
        const tokenResponse = await exchangeCodeForTokens(codeResponse.code);
        console.log("Token Response:", tokenResponse);

        if (!tokenResponse.id_token) {
          console.error("ID token is missing from the token response");
          handleLoginError("Google login failed. Please try again.");
          return;
        }

        const backendResponse = await fetch(LOGIN_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ google_token: tokenResponse.id_token }),
        });

        console.log("Backend response status:", backendResponse.status);
        const responseText = await backendResponse.text();
        console.log("Backend response text:", responseText);

        let data;
        try {
          data = JSON.parse(responseText);
        } catch (error) {
          console.error("Error parsing JSON:", error);
          handleLoginError("Server response is not valid JSON. Please try again later.");
          return;
        }

        if (data.status === 1) {
          handleLoginSuccess(data.data);
        } else {
          handleLoginError(data.message || "Google login failed. Please try again.");
        }
      } catch (error) {
        console.error("Error during Google login:", error);
        handleLoginError("Something went wrong. Please try again.");
      }
    },
    onError: (error) => {
      console.error('Google Login Failed:', error);
      handleLoginError("Google login failed. Please try again.");
    },
  });

  const exchangeCodeForTokens = async (code) => {
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        code: code,
        client_id: '387965829154-gnmj49v7bood1ibtg8t2901f5kpl7dvk.apps.googleusercontent.com',
        client_secret: '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',
        redirect_uri: 'http://localhost:3000',
        grant_type: 'authorization_code',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange code for tokens');
    }

    return response.json();
  };

  const handleLoginSuccess = (data) => {
    const { accessToken, refreshToken } = data;
    const decodedToken = jwtDecode(accessToken);
    const userId = decodedToken.data.userId;
  
    dispatch(setTokens({ accessToken, refreshToken, userId }));
    dispatch(setUser({
      userId: decodedToken.data.userId,
      firstName: decodedToken.data.fName,
      lastName: decodedToken.data.lName,
      email: decodedToken.data.email
    }));
    dispatch(setLoginSuccess("Login successful!"));
    toast.success("Login successful!");
  
    // Redirect to saved path or default to profile
    const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/profile';
    sessionStorage.removeItem('redirectAfterLogin'); // Clean up
    navigate(redirectPath);
  };


  const handleLoginError = (errorMessage) => {
    dispatch(setLoginError(errorMessage));
    dispatch(setLoginSuccess(""));
    toast.error(errorMessage);
  };

  const handleEmailChange = (e) => {
    dispatch(setEmail(e.target.value));
  };

  const handlePasswordChange = (e) => {
    dispatch(setPassword(e.target.value));
  };

  return (
    <div className="login-page">
      <div className="login-grid">
        <div className="login-image">
          <img src={loginImage} alt="Login Visual" />
        </div>
        <div className="login-form-container">
          <div className="login-form">
            <form onSubmit={handleSubmit}>
              <div className="styleName">Hello Again</div>
              <p>Welcome back! Please enter your details.</p>
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleEmailChange}
                maxLength="100"
                required
              />
              {emailError && <div className="error-message">{emailError}</div>}
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={handlePasswordChange}
                maxLength="20"
                required
              />
              {passwordError && <div className="error-message">{passwordError}</div>}
              {loginError && <div className="error-message">{loginError}</div>}
              {loginSuccess && <div className="success-message">{loginSuccess}</div>}
              <div className="forgot-password">
                <a href="/forgot-password">Forgot password</a>
              </div>
              <button type="submit" style={{ backgroundColor: "#EDA107" }}>
                Sign in
              </button>
              <button type="button" className="google-signin" onClick={() => login()}>
                <img src={googleLogo} alt="Google Logo" /> Sign in with Google
              </button>
              <div className="signup-link">
                Don't have an account? <a href="/signup">Sign up</a>
              </div>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default Login;